import React, { useState } from 'react';
import { Grid, Typography, Card, CardContent, DialogContentText, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DropzoneArea } from 'react-mui-dropzone';
import FormData from 'form-data';
import { useSnackbar } from 'notistack';
import fileDownload from 'js-file-download';
import { useSelector } from 'react-redux';
import { styles } from './styles';
import {
    useExportPositionsMutation,
    useDownloadPositionTemplateMutation,
    useDownloadPositionTemplateWithCompaFieldsMutation,
    useExportPositionsWithCompaFieldsMutation,
} from '../../../api/download';
import { useLoadPositionDataActionMutation } from '../../../api/positions';
import { Export as ExportIcon } from '../../../assets/icons/export';
import { hasPermission } from '../../../utils/permission';

export const UploadPositionData = () => {
    const [files, setFiles] = useState(null);
    const authorities = useSelector((state) => state.session.user.authorities);
    const userHasCompaExportPermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_EXPORT_POSITIONS_DATA',
    ]);
    const userHasCompaDownloadTemplatePermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_DOWNLOAD_POSITION_TEMPLATE',
    ]);

    const { enqueueSnackbar } = useSnackbar();

    const [exportPositions, { isLoading: isDownloading }] = useExportPositionsMutation();
    const [exportPositionsWithCompaFields, { isLoading: isDownloadingWithCompaFields }] =
        useExportPositionsWithCompaFieldsMutation();
    const [downloadPositionTemplate, { isLoading: isDownloadingTemplate }] = useDownloadPositionTemplateMutation();
    const [downloadPositionTemplateWithCompaFields, { isLoading: isDownloadingTemplateWithCompaFields }] =
        useDownloadPositionTemplateWithCompaFieldsMutation();
    const [loadPositionDataAction, { isLoading }] = useLoadPositionDataActionMutation();

    const handleFiles = (files) => {
        setFiles(files[0]);
    };

    const downloadPositionData = async () => {
        try {
            const response = userHasCompaExportPermisson
                ? await exportPositionsWithCompaFields().unwrap()
                : await exportPositions().unwrap();
            if (!response) return;
            if (response.status === 'ERROR') throw new Error(response?.message);

            fileDownload(new Blob([response]), 'positions.xlsx');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Download failed', { variant: 'error' });
        }
    };

    const handleUploadAction = async () => {
        if (!files) {
            enqueueSnackbar('Please select a file', {
                variant: 'error',
            });
            return;
        }
        try {
            const formData = new FormData();
            formData.append('dataFile', files);
            formData.append('isAdmin', true);

            const response = await loadPositionDataAction(formData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message, { variant: 'success' });
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Upload failed', { variant: 'error' });
        }
    };

    const downloadSampleFile = async () => {
        try {
            const response = userHasCompaDownloadTemplatePermisson
                ? await downloadPositionTemplateWithCompaFields().unwrap()
                : await downloadPositionTemplate().unwrap();
            if (!response) return;
            if (response.status === 'ERROR') throw new Error(response?.message);

            fileDownload(new Blob([response]), 'Template-PositionData.xlsx');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Download failed', { variant: 'error' });
        }
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Bulk Upload Position Data</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <Grid container justifyContent="center" alignItems="center" mt={1}>
                            <Grid item xs={12}>
                                <Box display="flex" xs="auto" mb={2} justifyContent="space-between">
                                    <DialogContentText>
                                        To download a template file
                                        <LoadingButton
                                            loading={isDownloadingTemplate || isDownloadingTemplateWithCompaFields}
                                            variant="text"
                                            color="primary"
                                            onClick={downloadSampleFile}
                                        >
                                            Click here
                                        </LoadingButton>
                                    </DialogContentText>
                                    <LoadingButton
                                        loading={isDownloading || isDownloadingWithCompaFields}
                                        color="secondary"
                                        disabled={false}
                                        onClick={downloadPositionData}
                                        size="medium"
                                        variant="text"
                                        startIcon={<ExportIcon fontSize="small" />}
                                    >
                                        Export Positions
                                    </LoadingButton>
                                </Box>
                                <DropzoneArea
                                    dropzoneText="Drag and drop a file here or click"
                                    filesLimit={1}
                                    onChange={handleFiles}
                                    showAlerts={false}
                                    showPreviews={true}
                                    showPreviewsInDropzone={false}
                                    useChipsForPreview
                                    previewGridProps={{
                                        container: {
                                            spacing: 1,
                                            direction: 'row',
                                        },
                                    }}
                                    previewText="Selected files"
                                />
                                <LoadingButton
                                    disabled={!files}
                                    sx={{
                                        mt: 3,
                                    }}
                                    loading={isLoading}
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUploadAction}
                                >
                                    Upload
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};
